// Vendor
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Components
import CompanyDetails from './components/CompanyDetails';

// Hooks
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import useCompany from 'src/features/company/hooks/useCompany';
import { useGetFeatureFlag } from 'src/features/featureFlag/hooks/useGetFeatureFlag';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';

// Redux
import { selectors as companySelector } from 'src/features/company/companySlice';

// Helpers
import { getCompanySettingsLink } from 'src/helpers/getCompanySettingsLink';

// Enums
import { FeatureFlagKey } from 'src/ts/enums';

const CompanyPage = (): JSX.Element => {
  const { onGetOne, onGetOneById, onSave, onResetUpdate } = useCompany();
  const { showSnackbar, VariantType } = useSnackbarProvider();
  const { onGetFeatureFlagValue } = useGetFeatureFlag();
  const { isAdmin } = useUserRoles();
  const { companyId } = useParams();

  const companyData = useSelector(companySelector.getOne.data);
  const companyIsLoading = useSelector(companySelector.getOne.isLoading);
  const companyIsIdle = useSelector(companySelector.getOne.isIdle);

  const companyUpdatedIsSaved = useSelector(companySelector.update.isSaved);
  const companyUpdatedIsLoading = useSelector(companySelector.update.isLoading);
  const companyUpdatedError = useSelector(companySelector.update.error);

  useEffect(() => {
    if (companyId) {
      onGetOneById(companyId);
    } else {
      onGetOne();
    }
  }, [onGetOne, onGetOneById, companyId]);

  const companyShortId = companyData?.short_id;
  // The shared flag store is keyed by flag name only, so a late response for a
  // previous company can overwrite it. Keep this company's value locally instead.
  const [settingsFlag, setSettingsFlag] = useState<{ shortId: string; enabled: boolean }>();

  useEffect(() => {
    if (!companyShortId) return undefined;
    let isCurrent = true;
    onGetFeatureFlagValue(
      FeatureFlagKey.SNAX_1331_SETTINGS_SERVICE_ENTITY_FEATURES,
      `companyShortId=${companyShortId}`
    ).then((action) => {
      if (!isCurrent) return;
      const result: unknown = (action as { payload?: { result?: unknown } }).payload?.result;
      setSettingsFlag({ shortId: companyShortId, enabled: result === true });
    });
    return () => {
      isCurrent = false;
    };
  }, [companyShortId, onGetFeatureFlagValue]);

  const isSettingsLinkEnabled =
    Boolean(companyShortId) &&
    settingsFlag !== undefined &&
    settingsFlag.shortId === companyShortId &&
    settingsFlag.enabled;
  const settingsLink =
    isAdmin && isSettingsLinkEnabled ? getCompanySettingsLink(companyData?.id) : null;

  useEffect(() => {
    if (companyUpdatedIsSaved) {
      onResetUpdate();
      showSnackbar(VariantType.success, 'The information has been edited successfully');
    }
    if (companyUpdatedError) {
      onResetUpdate();
      showSnackbar(VariantType.error, 'An error has ocurred, please refresh and try again');
    }
  }, [
    companyUpdatedIsSaved,
    companyUpdatedError,
    VariantType.success,
    VariantType.error,
    onResetUpdate,
    showSnackbar
  ]);

  return (
    <CompanyDetails
      company={companyData ?? undefined}
      isLoading={companyIsLoading || companyIsIdle}
      isSaving={companyUpdatedIsLoading}
      onSave={onSave}
      settingsLink={settingsLink}
    />
  );
};

export default CompanyPage;
